// Considered: SphereRefChipMolecule / PerspectiveAssigneesDisplayAtom — rejected: the chip is inline and
// single-action, the avatar stack is display-only; neither carries source line, access level, role nesting
// or a busy state. This row composes SphereAvatar + ChipAtom + ButtonAtom. See spec Reuse Audit.
import React, { useId, useState } from 'react';
import { useTranslation } from 'localization';
import type { InheritedMember, MemberAccessLevel, SphereMember } from 'types/sphereMembers';
import ButtonAtom from '../../atoms/Button/ButtonAtom';
import ChipAtom from '../../atoms/Chip/ChipAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import { useSphereInfo } from '../../../hooks/useSphereInfo';
import SphereAvatar from '../sphere-avatar/sphere-avatar';
import './MemberRowMolecule.scss';

export interface MemberRowMoleculeProps {
  member: SphereMember | InheritedMember;
  /** Localized perspective label, used in the Remove button's accessible name. */
  perspectiveLabel: string;
  /** Inherited rows: the strongest access SpiceDB allows this subject on the sphere. */
  level?: MemberAccessLevel;
  /** Inherited rows: where the access comes from, already localized. */
  sourceText?: string;
  /** Renders the Remove button; omitted (not disabled) when false. */
  canRemove?: boolean;
  /** A remove is in flight: the row dims and the button is `aria-busy`. */
  removing?: boolean;
  onRemove?: (subjectId: string) => void;
  /** Held through a role that is not listed beside it (hidden from the caller, or on another page). */
  throughRole?: boolean;
  /** Propagated rows of a role, nested under it behind a disclosure that starts collapsed. */
  children?: React.ReactNode;
}

const MemberRowMolecule: React.FC<MemberRowMoleculeProps> = ({
  member,
  perspectiveLabel,
  level,
  sourceText,
  canRemove = false,
  removing = false,
  onRemove,
  throughRole = false,
  children,
}) => {
  const { t } = useTranslation();
  const { info, loading } = useSphereInfo(member.subjectId);
  const name = info?.properties?.name;
  const displayName = name ?? t('sphere.members.unknownMember');
  const nestedCount = React.Children.count(children);
  const [nestedOpen, setNestedOpen] = useState(false);
  const nestedId = useId();

  return (
    <li
      className="member-row"
      data-testid="member-row"
      data-subject-id={member.subjectId}
      data-kind={member.kind}
      data-removing={removing || undefined}
      tabIndex={-1}
    >
      <div className="member-row__main">
        <SphereAvatar
          sphereId={member.subjectId}
          sphereName={displayName}
          imageId={info?.properties?.imageId}
          glyph={info?.properties?.glyph}
          glyphColor={info?.properties?.glyphColor}
          size="small"
          shape={member.kind === 'role' ? 'square' : 'circle'}
          decorative
        />
        <div className="member-row__identity">
          {loading && !info ? (
            <SkeletonAtom width="8rem" height="1rem" />
          ) : (
            <span className="member-row__name" data-unknown={!name || undefined}>
              {displayName}
            </span>
          )}
          {sourceText ? <span className="member-row__source">{sourceText}</span> : null}
        </div>
        <div className="member-row__tags">
          {member.kind === 'role' ? <ChipAtom label={t('sphere.members.roleTag')} /> : null}
          {throughRole ? <ChipAtom label={t('sphere.members.throughRole')} /> : null}
          {member.isSelf ? <ChipAtom label={t('sphere.members.you')} /> : null}
          {level ? <ChipAtom label={t(`sphere.members.grants.${level}`)} /> : null}
        </div>
        {canRemove && onRemove ? (
          <ButtonAtom
            className="member-row__remove"
            variant="ghost"
            shape="icon-square"
            icon="pi pi-times"
            type="button"
            loading={removing}
            aria-busy={removing}
            disabled={removing}
            aria-label={t('sphere.members.remove', { name: displayName, perspective: perspectiveLabel })}
            onClick={() => onRemove(member.subjectId)}
          />
        ) : null}
      </div>
      {nestedCount > 0 ? (
        <>
          <ButtonAtom
            className="member-row__nested-toggle"
            type="button"
            variant="text"
            size="small"
            icon={nestedOpen ? 'pi pi-chevron-down' : 'pi pi-chevron-right'}
            label={t('sphere.members.roleMembers', { name: displayName, count: nestedCount })}
            aria-expanded={nestedOpen}
            aria-controls={nestedId}
            onClick={() => setNestedOpen((open) => !open)}
          />
          {nestedOpen ? (
            <ul className="member-row__nested" id={nestedId}>
              {children}
            </ul>
          ) : null}
        </>
      ) : null}
    </li>
  );
};

export default MemberRowMolecule;
